import { deleteDB, IDBPDatabase, openDB } from 'idb'

export const DEFAULT_STORAGE_QUOTA_GAP_BYTES = 20 * 1024 * 1024;

type StorageQuotaOptions = {
  requiredBytes?: number;
  gap?: number;
};

const deleteTempQuotaDB = async (): Promise<void> => {
  try {
    await deleteDB('TempDB');
  } catch {
    // Best-effort cleanup: quota checks should report storage status, not cleanup failures.
  }
}

export const isStorageQuotaLimitReached = async ({
  requiredBytes = 0,
  gap = DEFAULT_STORAGE_QUOTA_GAP_BYTES,
}: StorageQuotaOptions = {}): Promise<boolean> => {
  const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined;

  if (storage?.estimate) {
    try {
      const { quota, usage } = await storage.estimate();
      if (quota != null && usage != null) {
        const availableBytes = quota - usage - gap;
        return requiredBytes > 0
          ? availableBytes < requiredBytes
          : availableBytes <= 0;
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'NotSupportedError') {
        return false;
      }
    }
  }

  if (requiredBytes > 0) {
    return false;
  }

  return await checkStorageQuotaThroughForceEntry(gap);
}

async function checkStorageQuotaThroughForceEntry(gap: number): Promise<boolean> {
  try {
    await createAndDeleteTempDBToTestQuota(gap);
  } catch (error) {
    await deleteTempQuotaDB();
    return error instanceof Error && error.name === 'QuotaExceededError';
  }

  return false;
}

async function createAndDeleteTempDBToTestQuota(stringSize: number): Promise<void> {
  const dbName = 'TempDB';
  const tableName = 'TempTable';
  const tableValue = { value: 'A'.repeat(stringSize) };
  const dbVersion = 1;

  await deleteTempQuotaDB();
  const db: IDBPDatabase<unknown> = await openDB(dbName, dbVersion, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(tableName)) {
        db.createObjectStore(tableName, { keyPath: 'id', autoIncrement: true });
      }
    },
  });
  try {
    const transaction = db.transaction(tableName, 'readwrite');
    const table = transaction.objectStore(tableName);
    await table.add(tableValue);
  } finally {
    db.close();
    await deleteTempQuotaDB();
  }
}
